function getEasternDateComponents(cell) {
    if (cell === null || cell === undefined || cell === "") return null;
    if (typeof cell === 'string') {
        const trimmed = cell.trim();
        const matchYMD = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matchYMD && (trimmed.length === 10 || /^\d{4}-\d{2}-\d{2}T00:00:00(\.000)?Z?$/.test(trimmed))) {
            return {
                year: parseInt(matchYMD[1], 10),
                month: parseInt(matchYMD[2], 10),
                day: parseInt(matchYMD[3], 10),
                yearStr: matchYMD[1],
                monthStr: matchYMD[2],
                dayStr: matchYMD[3]
            };
        }
    }
    const d = new Date(cell);
    if (isNaN(d.getTime())) return null;

    // If midnight UTC (from SQL DATE column), preserve the calendar date
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
        const iso = d.toISOString();
        return {
            year: parseInt(iso.slice(0, 4), 10),
            month: parseInt(iso.slice(5, 7), 10),
            day: parseInt(iso.slice(8, 10), 10),
            yearStr: iso.slice(0, 4),
            monthStr: iso.slice(5, 7),
            dayStr: iso.slice(8, 10)
        };
    }

    // If local midnight (from getLocalDate or DatePicker), preserve local date components
    if (cell instanceof Date && cell.getHours() === 0 && cell.getMinutes() === 0 && cell.getSeconds() === 0 && cell.getMilliseconds() === 0) {
        const year = cell.getFullYear();
        const month = cell.getMonth() + 1;
        const day = cell.getDate();
        return {
            year,
            month,
            day,
            yearStr: String(year),
            monthStr: String(month).padStart(2, '0'),
            dayStr: String(day).padStart(2, '0')
        };
    }

    // All dates are stored in Greenwich time (UTC), but user activity is based on Eastern Time
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    const parts = formatter.formatToParts(d);
    let yearStr = '', monthStr = '', dayStr = '';
    for (const part of parts) {
        if (part.type === 'year') yearStr = part.value;
        if (part.type === 'month') monthStr = part.value;
        if (part.type === 'day') dayStr = part.value;
    }
    return {
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
        day: parseInt(dayStr, 10),
        yearStr,
        monthStr,
        dayStr
    };
}

module.exports = {

    getEasternDateComponents: getEasternDateComponents,

    percentFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return `${parseFloat(cell).toFixed(2)}%`;
    },

    percentFormatterOneDecimal: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return `${parseFloat(cell).toFixed(1)}%`;
    },

    percentChangeFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return (parseFloat(cell) > 0 ? `+` : ``) + `${parseFloat(cell).toFixed(2)}%`;
    },

    percentChangeFormatterOneDecimal: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return (parseFloat(cell) > 0 ? `+` : ``) + `${parseFloat(cell).toFixed(1)}%`;
    },

    percentChangeFormatterNoDecimal: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return (parseFloat(cell) > 0 ? `+` : ``) + `${parseFloat(cell).toFixed(0)}%`;
    },

    boolFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (cell === true || cell === 1) return "✅";
        else return "❌"
    },

    percentFormatterNoDecimals: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return `${parseFloat(cell).toFixed(0)}%`;
    },

    percentFormatterOptionalDecimals: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        // check to determine if it's a whole number or if a decimal is needed
        if (cell % 1 === 0) {
            return `${parseFloat(cell).toFixed(0)}%`;
        } else {
            return `${parseFloat(cell).toFixed(1)}%`;
        }
    },

    condensedPercentFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else {
            let value = parseFloat(cell);
            if (value > -10 && value < 10) return `${value.toFixed(1)}%`;
            else return `${value.toFixed(0)}%`;
        }
    },

    moneyFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";

        let dollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });
        return dollar.format(cell);
    },

    moneyFormatterNoCents: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";

        let dollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        });
        return dollar.format(cell);
    },

    moneyChangeFormatterNoCents: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";

        let dollar = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        });
        let sign = cell > 0 ? "+" : "";
        return sign + dollar.format(cell);
    },

    intFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseInt(cell))) return "";
        else return `${parseInt(cell)}`;
    },

    decimalFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";
        else return `${parseFloat(cell).toFixed(2)}`;
    },

    signedDecimalFormatter: function (cell, row) {
        if (cell === null) return "";
        else if (isNaN(parseFloat(cell))) return "";

        let sign = cell > 0 ? "+" : "";
        return sign + `${parseFloat(cell).toFixed(2)}`;
    },

    jsonFormatter: function (cell, row) {
        if (cell === null) return "";
        else return JSON.stringify(cell);
    },

    dateFormmatter: function (cell, row) {
        const comp = getEasternDateComponents(cell);
        if (!comp) return "";
        return `${comp.month}/${comp.day}/${comp.year}`;
    },

    dateESTFormmatter: function (cell, row) {
        const comp = getEasternDateComponents(cell);
        if (!comp) return "";
        return `${comp.month}/${comp.day}/${comp.year}`;
    },

    dateYMDFormatter: function (cell, row) {
        const comp = getEasternDateComponents(cell);
        if (!comp) return "";
        return `${comp.yearStr}-${comp.monthStr}-${comp.dayStr}`;
    },

    datetimeESTFormmatter: function (cell, row) {
        if (cell === null || cell === undefined || cell === "") return "";
        const d = new Date(cell);
        if (isNaN(d.getTime())) return "";
        return `${d.toLocaleString('en-US', { timeZone: 'America/New_York' })}`;
    },

    dynamicFormatter: function (cell, row) {
        if (row.format === 'int')
            return module.exports.intFormatter(cell, row);
        else if (row.format === 'percent')
            return module.exports.percentFormatter(cell, row);
        else if (row.format === 'currency')
            return module.exports.moneyFormatter(cell, row);
        else if (row.format === 'date')
            return module.exports.dateFormmatter(cell, row);
        if (row.format === 'float')
            return module.exports.decimalFormatter(cell, row);
        else return cell;
    },

    dynamicFormatterWithFormat: function (cell, row, format) {
        if (format === 'int')
            return module.exports.intFormatter(cell, row);
        else if (format === 'percent')
            return module.exports.percentFormatter(cell, row);
        else if (format === 'currency')
            return module.exports.moneyFormatter(cell, row);
        else if (format === 'date')
            return module.exports.dateFormmatter(cell, row);
        if (format === 'float')
            return module.exports.decimalFormatter(cell, row);
        else return cell;
    },

    numberSort: function (a, b, order, sortField) {   // order is desc or asc
        if (order === 'desc') {
            return a[sortField] - b[sortField];
        } else {
            return b[sortField] - a[sortField];
        }
    },

    dateSort: function (a, b, order, sortField) {   // order is desc or asc
        if (order === 'desc') {
            return new Date(a[sortField]) - new Date(b[sortField]);
        } else {
            return new Date(b[sortField]) - new Date(a[sortField]);
        }
    },

    getMaxChartValue: function (chartData, index) {
        let max = 0;
        chartData.forEach(d => {
            if (d[index] > max) max = d[index];
        })
        return max;
    },

    getMinChartValue: function (chartData, index) {
        let min = 0;
        chartData.forEach(d => {
            if (d[index] < min) min = d[index];
        })
        return min;
    }
}
